import { useState } from 'react';
import { useRxCollection, useRxDocument } from 'rxdb/plugins/react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { entityNameOfCollection, usePersonLabels } from '../users/personLabels';
import {
    clampFindingType,
    DEFAULT_FINDING_TYPE,
    type FindingType
} from '@ast-data/shared';
import {
    dialogCloseStep,
    useChainSteps,
    usePagedSteps,
    type CloseScope
} from '../../components/dialogBackTrail';

// The hooks behind the office record dialog ROUTES (see recordDialogRoutes):
// closing, the frozen snapshot a dialog keeps editing, the per-record remount
// key and the "new, seeded from this record" lookup.

/**
 * Close = leave the dialog route. Opened in-app, going BACK restores the page
 * underneath; a deep link has no in-app history entry, so it replaces itself with
 * the parent page instead of leaving the site.
 *
 * Paging to the next/previous record leaves the record it came from standing in
 * history, so closing must step over ALL of them: the user left the list, not the
 * twelfth day they paged past. The count rides along in the router state (see
 * `useSiblingNavState`).
 *
 * Schließen leaves the dialogs this one was reached from as well: the user
 * wants the list they started on, not ten presses through Befund, Planum and
 * Profil. Only a `step` (the back crumb, browser back, a save) returns to the
 * dialog before. Anything but `'step'` — a click event from the loading shell —
 * closes the chain.
 */
export function useDialogClose(parentPath: string) {
    const navigate = useNavigate();
    const location = useLocation();
    const paged = usePagedSteps();
    const chain = useChainSteps();
    return (scope?: CloseScope) => {
        // The unsaved-changes guard parks a sentinel entry ON TOP of the
        // dialog's route entry while the dialog is open, so leaving must step
        // over BOTH — one step sheds the sentinel and stays routed.
        const onSentinel = Boolean(
            (window.history.state as { dialogGuard?: boolean } | null)
                ?.dialogGuard
        );
        const step = dialogCloseStep({
            onSentinel,
            isDeepLink: location.key === 'default',
            paged,
            chain,
            scope: scope === 'step' ? 'step' : 'chain'
        });
        if (step.kind === 'parent') {
            navigate(parentPath, { replace: true });
        } else {
            navigate(-step.steps);
        }
    };
}

/**
 * The record behind an edit URL, frozen at first arrival: the RxDB query is live,
 * but a doc identity change mid-edit (any background sync) would re-run the
 * dialogs' reset-on-open effects and wipe the user's typing.
 */
export function useRecordSnapshot<T>(
    collectionName: string,
    id: string
): T | null {
    const collection = useRxCollection<T>(collectionName);
    const { result: doc } = useRxDocument<T>(collection, id || undefined);
    // The frozen doc belongs to the id it was frozen FOR. Both routes of a kind
    // render the same component (see useDialogKey), so this hook survives a route
    // change: without the id beside it, the editor that "Teil hinzufügen" opens
    // kept the Teil it came from — a `new` route saving over an existing record.
    const [snapshot, setSnapshot] = useState<{ id: string; doc: T } | null>(
        null
    );
    // The doc must be the one that was ASKED for: after the id changes the query
    // still emits the previous record for a render or two, and freezing that
    // under the new id would stick.
    const emitted = doc?.toMutableJSON() as (T & { id?: unknown }) | undefined;
    /**
     * The editor shows the record's people by their accounts, like the grid
     * (usePersonLabels) — and saving writes that name, so a stored "K.Hecht"
     * heals the first time the record is saved. The names are part of what is
     * frozen, so nothing is frozen before they are ready: labelling a frozen
     * record afterwards gave it a new identity whenever a profile synced, and
     * the dialog reset itself over the user's typing.
     */
    const personLabels = usePersonLabels();
    const live =
        id && emitted?.id === id && personLabels.ready
            ? personLabels.show(
                  entityNameOfCollection(collectionName),
                  emitted as T & object
              )
            : null;
    // Freeze the first arrival DURING render, not in an effect: the dialog mounts
    // the moment the local query emits, a render earlier. The guard fires the
    // one-shot set exactly once per id, so a later live change (background sync,
    // a renamed account) is ignored.
    if (snapshot && snapshot.id !== id) {
        setSnapshot(null);
    } else if (!snapshot && live) {
        setSnapshot({ id, doc: live });
    }
    return snapshot?.id === id ? snapshot.doc : live;
}

/**
 * The remount key for a dialog route. `<Route element>` for `…/planums/new` and
 * `…/planums/:id/edit` is the SAME component, so React reconciles the two instead
 * of remounting: the editor "Teil hinzufügen" navigates to WAS the previous
 * Teil's editor, form values, close-guard prompt and all. Keyed by the record it
 * edits, each dialog is a fresh one.
 */
export function useDialogKey(mode: 'new' | 'edit', id: string): string {
    const [params] = useSearchParams();
    return mode === 'edit'
        ? `edit:${id}`
        : // The whole query is in the key: „Neuer Befund" and „Neuer Befund
          // baulicher Bestand" are the same route (`?type=`), as are a new
          // record seeded from another (`?seedFrom=`) or preset to reference a
          // description (`?refPlanum=`), and a dialog still holding the other
          // one's answers would ask the wrong questions.
          `new:${params.toString()}`;
}

/**
 * The type a NEW Befund is being created as: `…/findings/new?type=structure`
 * makes it a baulicher Bestand, anything else the ordinary Befundbeschreibung.
 *
 * A SEARCH PARAM, not router state: a form left standing in the field has to come
 * back as what it was opened as. Read only while creating — an existing Befund
 * carries its type in the record, and the type never changes.
 */
export function useNewFindingType(mode: 'new' | 'edit'): FindingType {
    const [params] = useSearchParams();
    return mode === 'new'
        ? clampFindingType(params.get('type'))
        : DEFAULT_FINDING_TYPE;
}

/**
 * The description a new one is seeded from: "Teil hinzufügen" links to
 * `…/new?seedFrom=<id>`, and the form copies that record's Lage, Form and
 * Bodenmerkmale into the empty Teil. A search param so the seed survives a
 * reload. Null while editing, and null for a plain new description.
 */
export function useSeedRecord<T>(
    collectionName: string,
    mode: 'new' | 'edit'
): T | null {
    const [params] = useSearchParams();
    return useRecordSnapshot<T>(
        collectionName,
        mode === 'new' ? (params.get('seedFrom') ?? '') : ''
    );
}

/**
 * Whether a `new` dialog is still waiting for the record it is seeded from
 * because the people's names have not loaded: the seed is frozen with them (see
 * useRecordSnapshot), and a dialog mounted before it would reset over what
 * the user has typed once it arrives. Like the edit routes, the route shows the
 * empty shell meanwhile. False once the names are in — a seed that never comes
 * (a deleted record) then opens the dialog unseeded, as it always did.
 */
export function useSeedWaiting(mode: 'new' | 'edit', seed: unknown): boolean {
    const [params] = useSearchParams();
    const { ready } = usePersonLabels();
    return mode === 'new' && Boolean(params.get('seedFrom')) && !seed && !ready;
}
